"use client";

import { Editor, Path, Element as SlateElement, Transforms } from "slate";
import { ReactEditor } from "slate-react";
import type { ImageAlign, ImageElement } from "../elements/types";

// ─── insertBlockNode ──────────────────────────────────────────────────────────

export const insertBlockNode = (editor: any, node: any): Path | null => {
  const { selection } = editor;
  if (!selection) return null;

  const [blockMatch] = Array.from(
    Editor.nodes(editor, {
      at: selection,
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && Editor.isBlock(editor, n),
      mode: "lowest",
    }),
  );

  let insertedPath: Path | null = null;

  // A caret inside a table cell or a toggle title cannot host a block (it would be nested into the row or split
  // the title), so the new block goes right after the whole table / toggle instead.
  const containerEntry = Editor.above(editor, {
    at: selection,
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && ["table-cell", "toggle-title"].includes((n as any).type),
    mode: "lowest",
  });

  if (containerEntry) {
    const [containerNode, containerPath] = containerEntry;
    // table-cell > table-row > table, toggle-title > toggle-list
    const wrapperPath = (containerNode as any).type === "table-cell" ? Path.parent(Path.parent(containerPath)) : Path.parent(containerPath);
    insertedPath = Path.next(wrapperPath);
    Transforms.insertNodes(editor, node, { at: insertedPath });
  } else if (blockMatch) {
    const [blockNode, blockPath] = blockMatch;
    const isEmptyParagraph =
      (blockNode as any).type === "paragraph" && (blockNode as any).children?.length === 1 && (blockNode as any).children[0].text === "";

    if (isEmptyParagraph) {
      Transforms.insertNodes(editor, node, { at: blockPath });
      insertedPath = blockPath;
    } else {
      Transforms.insertNodes(editor, node);
      const [match] = Array.from(Editor.nodes(editor, { match: (n) => n === node }));
      if (match) {
        insertedPath = match[1];
      }
    }
  } else {
    Transforms.insertNodes(editor, node);
    const [match] = Array.from(Editor.nodes(editor, { match: (n) => n === node }));
    if (match) {
      insertedPath = match[1];
    }
  }

  if (insertedPath) {
    const nextPath = Path.next(insertedPath);
    let nextNode = null;
    try {
      nextNode = Editor.node(editor, nextPath)[0];
    } catch (_) {}

    if (!nextNode || (nextNode as any).type !== "paragraph") {
      Transforms.insertNodes(editor, { type: "paragraph", children: [{ text: "" }] } as any, { at: nextPath });
    }
    try {
      Transforms.select(editor, Editor.start(editor, nextPath));
    } catch (_) {}
  }

  // Always focus the editor after inserting a block node
  try {
    ReactEditor.focus(editor);
  } catch (_) {}

  return insertedPath;
};

// ─── Specific insert helpers ───────────────────────────────────────────────────

export const insertImage = (editor: any, url: string, caption?: string, width?: number, align?: ImageAlign) => {
  const text = { text: "" };
  const image: ImageElement = {
    type: "image",
    url,
    caption,
    width,
    align,
    children: [text],
  };
  insertBlockNode(editor, image);
};

export const insertInlineImage = (editor: any, url: string, alt?: string) => {
  const inlineImage: any = {
    type: "inline-image",
    url,
    alt: alt || "",
    children: [{ text: "" }],
  };
  Transforms.insertNodes(editor, inlineImage);
};

export const insertHorizontalRule = (editor: any) => {
  const hr = { type: "horizontal-rule", children: [{ text: "" }] };
  insertBlockNode(editor, hr);
};

export const insertTable = (editor: any) => {
  const table = {
    type: "table",
    colWidths: [210, 210],
    children: [
      {
        type: "table-row",
        children: [
          { type: "table-cell", children: [{ text: "Cell 1" }] },
          { type: "table-cell", children: [{ text: "Cell 2" }] },
        ],
      },
      {
        type: "table-row",
        children: [
          { type: "table-cell", children: [{ text: "Cell 3" }] },
          { type: "table-cell", children: [{ text: "Cell 4" }] },
        ],
      },
    ],
  };
  insertBlockNode(editor, table);
};

export const insertToggleList = (editor: any, titleText?: string) => {
  const toggle = {
    type: "toggle-list",
    children: [
      { type: "toggle-title", children: [{ text: titleText || "" }] },
      { type: "toggle-content", children: [{ type: "paragraph", children: [{ text: "" }] }] },
    ],
  };

  const insertedPath = insertBlockNode(editor, toggle);

  if (insertedPath) {
    const titleTextPath = [...insertedPath, 0, 0];
    try {
      Editor.node(editor, titleTextPath);
      Transforms.select(editor, { anchor: { path: titleTextPath, offset: 0 }, focus: { path: titleTextPath, offset: 0 } });
    } catch (_) {}
  }
};

export const insertTweet = (editor: any, url: string) => {
  const tweet = {
    type: "tweet",
    url,
    tweetText: 'Click "View live" to see the live tweet.',
    children: [{ text: "" }],
  };
  insertBlockNode(editor, tweet);
};

export const insertYoutube = (editor: any, url: string) => {
  let videoId = url;
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtube.com")) {
      videoId = parsed.searchParams.get("v") || url;
    } else if (parsed.hostname.includes("youtu.be")) {
      videoId = parsed.pathname.substring(1);
    }
  } catch (_) {}
  const youtube = { type: "youtube", videoId, url, children: [{ text: "" }] };
  insertBlockNode(editor, youtube);
};

export const insertEmbedCard = (
  editor: any,
  meta: {
    url: string;
    title?: string;
    description?: string;
    image?: string;
    author?: string;
    domain?: string;
    siteName?: string;
  },
) => {
  let computedDomain = meta.domain;
  if (!computedDomain && meta.url) {
    try {
      computedDomain = new URL(meta.url).hostname.replace(/^www\./, "");
    } catch {}
  }
  const embedNode = {
    type: "embed-card",
    url: meta.url,
    title: meta.title || meta.url,
    description: meta.description || "",
    image: meta.image || "",
    author: meta.author || "",
    domain: computedDomain || "link",
    siteName: meta.siteName || computedDomain,
    children: [{ text: "" }],
  };
  insertBlockNode(editor, embedNode);
};

export const insertSpaceAbove = (editor: any) => {
  const { selection } = editor;
  if (!selection) return;
  const [match] = Array.from(
    Editor.nodes(editor, {
      at: selection,
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n),
      mode: "lowest",
    }),
  );
  if (match) {
    const [, path] = match;
    Transforms.insertNodes(editor, { type: "paragraph", children: [{ text: "" }] } as any, { at: path });
  }
};
